import cn from 'classnames';
import React, { memo, useCallback, useEffect } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';

import { InputV2 } from 'components/ComponentV2';
import { Input } from 'components/form';
import IconButton from 'components/IconButton';

import { PlaceAttributes } from 'lib/models/event';

import styles from './AutoComplete.module.scss';

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

type AddressComponents = {
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  streetAddress: string | null;
  addressLocality: string | null;
};

export interface AutoCompleteProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  placeAttributes: PlaceAttributes | null;
  fetchAddressComponents: (
    value: google.maps.places.PlaceResult
  ) => AddressComponents;
  setPlaceAttributes: (data: PlaceAttributes | null) => void;
  v2?: boolean;
  labelClassName?: string;
}

export const AutoComplete: React.FC<AutoCompleteProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    fetchAddressComponents,
    setPlaceAttributes,
    placeAttributes,
    v2 = false,
    labelClassName,
  }: AutoCompleteProps) => {
    const contentClassNames = cn(
      {
        [styles.AutoComplete]: true,
      },
      styles.inputLocation,
      className
    );

    const {
      ref: locationRef,
    }: {
      ref: React.RefObject<HTMLInputElement | null>;
    } = usePlacesWidget({
      apiKey: GOOGLE_MAPS_API_KEY,
      options: {
        types: [],
        fields: [
          'address_components',
          'geometry.location',
          'place_id',
          'name',
          'formatted_address',
        ],
      },
      onPlaceSelected: (place) => {
        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();
        const address = `${place.name}, ${place.formatted_address}`;
        const google_identifier = place.place_id;

        if (locationRef.current) locationRef.current.value = address;

        const { city, country, zip, addressLocality, streetAddress } =
          fetchAddressComponents(place);
        setPlaceAttributes({
          google_identifier,
          address,
          latitude,
          longitude,
          city: city as string,
          country: country as string,
          street_address: streetAddress as string,
          address_locality: addressLocality as string,
          zipcode: zip as string,
        });
      },
    });

    useEffect(() => {
      if (locationRef.current) {
        locationRef.current.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
          }
        });
      }
    }, [locationRef]);

    const onClear = useCallback(() => {
      setPlaceAttributes(null);
      if (locationRef.current) {
        locationRef.current.value = '';
      }
    }, [locationRef]);

    const onChange = useCallback(
      (event) => {
        if (!event.target.value) {
          onClear();
        }
      },
      [onClear]
    );

    return (
      <>
        {v2 ? (
          <InputV2
            className={contentClassNames}
            style={style}
            data-testid="AutoComplete"
            ref={locationRef}
            label="Location"
            placeholder="Add a location"
            defaultValue={placeAttributes?.address}
            rightIcon={
              placeAttributes?.address ? (
                <IconButton
                  type="button"
                  iconName="icon_close"
                  size="xsmall"
                  onClick={onClear}
                  iconClassName={styles.clearAddress}
                />
              ) : null
            }
            onChange={onChange}
            labelClassName={labelClassName}
          />
        ) : (
          <Input
            className={contentClassNames}
            style={style}
            data-testid="AutoComplete"
            ref={locationRef}
            label="Location"
            placeholder="Add a Location"
            defaultValue={placeAttributes?.address}
            endIcon={placeAttributes?.address ? 'cancel' : null}
            endIconSize="xsmall"
            endIconClassName={styles.cancel}
            onEndIconClick={onClear}
            onChange={onChange}
          />
        )}
      </>
    );
  }
);

AutoComplete.displayName = 'AutoComplete';
